import {
  useLDClient,
  useLDFlag,
  type LDClient,
  type LDContext,
} from 'launchdarkly-vue-client-sdk'
import type { RouteLocationNormalized } from 'vue-router'
import { useAuth } from '@/composables/useAuth.js'

/**
 * Client initialization timeout in seconds.
 * https://launchdarkly.com/docs/sdk/client-side/vue#set-the-initialization-timeout
 */
const INITIALIZATION_TIMEOUT = 2

export const CLIENT_CREATION_MIGRATION =
  'temporary.new_frontend.client_creation'
export const PATIENT_CREATION_MIGRATION =
  'temporary.new_frontend.patient_creation'
export const REMINDER_CREATION_RULES_EDIT =
  'temporary.new_frontend.reminder_creation_rules_edit'
export const REMINDER_PATIENT_CREATION_REMINDERS =
  'temporary.reminder.patient_creation_reminders'
export const REMINDER_CREATION_RULE_RECURRING =
  'temporary.reminder.creation_rule_recurring'

/**
 * Possible feature flag keys with their defaults.
 *
 * These should be conservative.
 * They are used if the flag cannot be found or if LaunchDarkly can't be reached.
 * They should be `false` most of the time (if type is a boolean).
 *
 * Once temporary flags are 100% rolled out they should be removed.
 */
const defaultFlagValues = {
  'permanent.test': false,
  'temporary.new_frontend.ai_text_refinement': false,
  'temporary.new_frontend_migration.multiple_stock_move': false,
  'temporary.non-existent-string-value-example': 'group-1',
  'temporary.new_frontend_migration.patient_locations': false,
  'temporary.organization.patient_location_capacity': false,
  'temporary.new_frontend.clients': false,
  'permanent.aha.enabled': false,
  'temporary.zendesk.messenger.widget': false,
  'temporary.supersearch.department_group_filter': false,
  'temporary.centralized_settings': false,
  'temporary.pendo': false,
  'temporary.new_frontend.merged_patients': false,
  'temporary.new_frontend.client_statements_revamp': false,
  'temporary.new_frontend.client_statements_post_mvp': false,
  'temporary.new_frontend.client_statements_current_balance': false,
  'temporary.new_frontend.client_statements_quantity_display': false,
  'temporary.statements.client_statements_subtotal_column': false,
  'temporary.new_frontend_migration.catalog_price_categories': false, //item-categories
  'temporary.new_frontend.hide_financial_data': false, // Pets at home: Billing tab
  'temporary.revision_log_client_patient': false,
  'temporary.new_frontend.digital_practice_chat_widget': false,

  // New consultation page
  'temporary.new_frontend.consultation_overview': true,
  'temporary.new_frontend.consultation_outpatient': true,
  'temporary.new_frontend.consultation_treatment_items.procedures_creation': false,

  [CLIENT_CREATION_MIGRATION]: false,
  [PATIENT_CREATION_MIGRATION]: false,
  [REMINDER_CREATION_RULES_EDIT]: false,
  [REMINDER_PATIENT_CREATION_REMINDERS]: false,
  [REMINDER_CREATION_RULE_RECURRING]: false,
  'temporary.new_frontend.manual_health_service_reminders': false,
  'temporary.new_frontend.digital_practice_chat_widget.iframe_url': '',
  'temporary.new_frontend.digital_practice_chat_widget.iframe_path': '',
  'temporary.new_frontend.health_plans': false,
  'temporary.global_search.full_page': false,
  'temporary.global_search.display_as_modal': false,
  'temporary.new_frontend.client_transactions_billing_notes': false,
  'temporary.new_frontend_migration.wholesalers': false,
  'temporary.stock.use_wholesaler_name_postfix': false,
  'temporary.new_frontend.locum_management': false,
  'temporary.new_frontend.new_settings_menu': false,
  'temporary.new_frontend.ai_chatbot': false,
  'temporary.new_frontend.ai_scribe_custom_templates': false,
  'temporary.new_frontend.ai_scribe_qr_code': false,
  'temporary.new_frontend.client_patient_billing': false, // Client patient: Billing tab
  'temporary.new_frontend.clinic_staff_table': false,
  'temporary.new_frontend.registration_custom_fields': false,
  'temporary.new_frontend.home_delivery': false,
  'temporary.new_frontend.prepayment_invoice_rows': false,
  'temporary.new_frontend.invoice_run': false,

  // New calendar experience
  'temporary.new_frontend.new_calendar': false,

  // Reports
  'temporary.new_frontend.reporting': false,
  'temporary.new_frontend.data_practice_performance_overview': false,

  'temporary.practice_performance_reports.hide_consultation_count_report': false,
  'temporary.practice_performance_reports.hide_treated_species_report': false,
  'temporary.practice_performance_reports.hide_appointments_report': false,
  'temporary.practice_performance_reports.hide_consultation_time_report': false,
  'temporary.practice_performance_reports.hide_diagnosis_report': false,
  'temporary.practice_performance_reports.hide_bereavement_report': false,
  'temporary.practice_performance_reports.hide_new_clients_report': false,
  'temporary.practice_performance_reports.hide_vaccination_report': false,

  'temporary.financial_reports.hide_accounts_receivable_report': true, // Incomplete report
  'temporary.financial_reports.hide_invoices_report': true,
  'temporary.financial_reports.hide_payments_report': true,
  'temporary.financial_reports.hide_prepayments_report': true,
  'temporary.financial_reports.hide_provet_pay_report': true,
  'temporary.financial_reports.provet_pay_report_rework': false,
  'temporary.financial_reports.hide_sales_by_payment_report': true,
  'temporary.financial_reports.hide_item_purchases_report': true,
  'temporary.financial_reports.hide_item_sales_report': true,
  'temporary.financial_reports.hide_item_price_change_report': true,
  'temporary.financial_reports.hide_discounts_report': true,
  'temporary.financial_reports.hide_discount_invoices_report': true,
  'temporary.financial_reports.hide_commissions_report': true,
  'temporary.financial_reports.hide_gross_profit_report': true,
  'temporary.financial_reports.hide_debtors_creditors_report': true,
  'temporary.financial_reports.hide_unpaid_invoices_report': true,
  'temporary.financial_reports.hide_bank_payment_processing_report': true,
  'temporary.financial_reports.hide_month_end_report': true,
  'temporary.financial_reports.hide_eprescription_fees_report': true,
  'temporary.financial_reports.hide_central_laboratory_referrals_report': true,
  'temporary.financial_reports.hide_central_laboratory_month_end_report': true,
  'temporary.financial_reports.hide_register_payments_report': true,
  'temporary.financial_reports.hide_general_ledger_report': true,
  'temporary.financial_reports.hide_electronic_journal_report': true,

  'temporary.stock_management_reports.hide_stock_level_report': true,
  'temporary.stock_management_reports.hide_stock_item_usage_report': true,
  'temporary.stock_management_reports.hide_medicine_usage_report': true,
  'temporary.stock_management_reports.hide_inventory_report': true,
  'temporary.stock_management_reports.hide_cabinet_discrepancy_report': true,

  'temporary.communication_reports.hide_feedback_report': true,
  'temporary.communication_reports.hide_sent_referrals_report': true,
  'temporary.communication_reports.hide_sent_sms_messages_report': true,

  'temporary.lists_reports.hide_item_price_list_report': true,

  'temporary.business_intelligence_reports.hide_business_intelligence_report': true,

  'temporary.conversation': false,

  'temporary.new_frontend_migration.invoice_finished': false,
  'temporary.vetera_branding': false,
}

type FlagDefaults = typeof defaultFlagValues
export type FlagKey = keyof FlagDefaults
export type FlagSet = Partial<FlagDefaults>

function getDefaultFlagValue<T extends FlagKey>(flagKey: T) {
  return defaultFlagValues[flagKey]
}

/**
 * Use feature flag value that subscribes to streamed updates (except in dev or test mode).
 * Don't use this in middleware.
 */
export function useFeatureFlag<T extends FlagKey>(
  flagKey: T,
): Readonly<Ref<FlagDefaults[T]>> {
  return useLDFlag(flagKey, getDefaultFlagValue(flagKey))
}

/**
 * Guard a page with a feature flag, redirecting to a page in the Legacy Frontend if the feature flag is disabled.
 */
export function useFeatureFlagGuard(
  flagKey: FlagKey,
  options: { redirectUrl: URL },
) {
  const featureFlag = useFeatureFlag(flagKey)

  watchEffect(() => {
    if (isIdentified.value && featureFlag.value === false) {
      return navigateTo(options.redirectUrl.toString(), {
        external: true,
        redirectCode: 307,
      })
    }
  })
}

const handleInitializationError = (error: Error) => {
  if (error?.name.toLowerCase().includes('timeout')) {
    console.error(
      `Feature flag client initialization timed out with error: ${error}`,
    )
  } else {
    console.error(`Feature flag client initialization error: ${error}`)
  }
}

let client: LDClient | null = null

const isIdentified = ref(false)
const launchDarklyStreamId = ref('')

/**
 * Returns a reactive ref indicating if LaunchDarkly has finished identifying the user.
 * Use this to wait for feature flags to be ready before making decisions based on them.
 * Similar to VueUse's `useMounted` composable.
 */
export const useLaunchDarklyReady = () => ({
  isLDReady: readonly(isIdentified),
})

/**
 * Lifecycle hook that runs once when LaunchDarkly has finished identifying the user.
 * Similar to `onMounted`, but waits for feature flags to be ready.
 *
 * @param callback - Function to run when LaunchDarkly is ready. Can be async.
 */
export const onLaunchDarklyReady = (callback: () => void | Promise<void>) => {
  // If already identified, run the callback immediately
  if (isIdentified.value) {
    return callback()
  }

  const stopWatcher = watch(isIdentified, async (isReady) => {
    if (!isReady) {
      return
    }

    stopWatcher()
    await callback()
  })
}

export function useFeatureFlagClient(to?: RouteLocationNormalized) {
  const config = useRuntimeConfig()
  const route = to || useRoute()
  const provetId = route.params.provetId as string
  const { user, activeDepartment, organization } = useAuth()

  const streamId = computed(() => {
    // When an error page is rendered, there is no user, department, or organization
    // so we need to return a special value to avoid errors
    if (!route.matched.length) {
      return 'error-page'
    }

    return `${user.value?.id || 'user'}@${activeDepartment.value?.id || 'department'}@${organization.value?.id || 'organization'}@${provetId}`
  })

  const context = computed<LDContext>(() => {
    const userKey = user?.value?.attributes?.key

    return {
      kind: 'user',
      key: userKey ?? 'anonymous',
      anonymous: !userKey,
      profile_language: user?.value?.attributes?.language ?? undefined,
      firstName: user?.value?.attributes?.first_name,
      lastName: user?.value?.attributes?.last_name,
      email: user?.value?.attributes?.email,
      provet_id: parseInt(provetId),
      department_id: parseInt(activeDepartment?.value?.id ?? '0'),
      department_country: activeDepartment?.value?.attributes?.country,
      work_role: user?.value?.attributes?.work_role_code ?? undefined, // The legacy frontend & backend set the work_role as an integer, so we need to do the same
      is_admin: user?.value?.attributes?.is_admin,
      is_paying_customer:
        organization.value?.attributes?.is_paying_customer ?? undefined,
      release_version: config.public.appVersion,
      environment: config.public.sentry.environment,
      _meta: {
        privateAttributes: ['email'],
      },
    }
  })

  const updateLaunchDarklyContext = async (streamId: string) => {
    if (!client) {
      client = useLDClient()

      if (!client) {
        if (
          process.env.NODE_ENV !== 'production' ||
          process.env.MIRAGE_ENABLED === 'true'
        ) {
          const { ldLogWarn } = await import('@/utils/dev-log/index.js')
          ldLogWarn('Client not available, skipping context update')
        }
        return
      }
    }

    if (
      process.env.NODE_ENV !== 'production' ||
      process.env.MIRAGE_ENABLED === 'true'
    ) {
      const { ldLog } = await import('@/utils/dev-log/index.js')
      ldLog({ title: 'Stream ID', label: streamId, method: 'groupCollapsed' })
      console.table(context.value)
      console.groupEnd()
    }

    try {
      await client.waitForInitialization(INITIALIZATION_TIMEOUT)
      await client.identify(context.value)
    } catch (error) {
      handleInitializationError(error as Error)
    }

    // Set fallback values to signal readiness even on error, so guards/watchers don't hang.
    isIdentified.value = !context.value.anonymous
    launchDarklyStreamId.value = streamId
  }

  /**
   * Initialize LaunchDarkly client and identify the user.
   * Called only from the middleware during initial page load.
   * Subsequent context changes are handled by the watchDebounced below.
   */
  const initialize = async () => {
    if (launchDarklyStreamId.value === streamId.value || !provetId) {
      return
    }

    // First, get the LaunchDarkly client instance from the Vue plugin
    client = useLDClient()

    // Then, wait for user data and identify the user context to LaunchDarkly
    await updateLaunchDarklyContext(streamId.value)
  }

  /**
   * Re-identify the user to LaunchDarkly when the context changes during the app lifecycle.
   * The streamId changes when user, department, or organization changes
   * (e.g., switching departments). Debounced to avoid excessive API calls.
   */
  watchDebounced(
    streamId,
    async (newStreamId) => {
      if (launchDarklyStreamId.value === newStreamId) {
        return
      }

      await updateLaunchDarklyContext(newStreamId)
    },
    { debounce: 300 },
  )

  return {
    initialize,
    updateLaunchDarklyContext: (streamIdParam?: string) =>
      updateLaunchDarklyContext(streamIdParam ?? streamId.value),
    isIdentified: unref(isIdentified),

    /**
     * Get feature flag value that does not subscribe to streamed updates.
     * Should be used in middleware or in plugin setup function.
     */
    async getVariation<T extends FlagKey>(
      flagKey: T,
    ): Promise<FlagDefaults[T]> {
      await client
        ?.waitForInitialization(INITIALIZATION_TIMEOUT)
        .catch(handleInitializationError)

      return (
        client?.variation(flagKey, getDefaultFlagValue(flagKey)) ??
        getDefaultFlagValue(flagKey)
      )
    },

    /**
     * Wait for the client to be initialized.
     *
     * @param timeout - The timeout in seconds.
     */
    async waitForInitialization(timeout: number = INITIALIZATION_TIMEOUT) {
      await client
        ?.waitForInitialization(timeout)
        .catch(handleInitializationError)
    },
  }
}
